// choose-optional-holiday-dialog.component.ts

import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ColDef, GridApi } from 'ag-grid-enterprise';
import { dateFilterParamsLong } from '@rcs/utils';
import { ErsGridComponent } from '@rcs/grid';
import { HolidayRow, Supervisor } from '../../models/leave.model';
import { LeaveService } from '../../services/leave.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSortByPipe } from '@rcs/ers-elementals';

export interface ChooseOptionalHolidayDialogData {
  availableHolidays: HolidayRow[];
  /** Local numeric IDs of holidays to pre-select (update mode) */
  preSelectedIds?: number[];
  /** When true the dialog was opened in edit/update mode */
  isUpdateMode?: boolean;
  /** Pre-fill the supervisor dropdown */
  previousSupervisorId?: string;
}

export interface ChooseOptionalHolidayDialogResult {
  selectedHolidayIds: number[];
  supervisorId: string;
  supervisorName: string;
}

const CELL_STYLE = {
  fontSize: '13px',
  color: '#1e293b',
  display: 'flex',
  alignItems: 'center',
};

@Component({
  selector: 'ers-choose-optional-holiday-dialog',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    ErsGridComponent,
    ErsSortByPipe,
  ],
  templateUrl: './choose-optional-holiday-dialog.component.html',
  styleUrls: ['./choose-optional-holiday-dialog.component.scss'],
})
export class ChooseOptionalHolidayDialogComponent implements OnInit {
  year = new Date().getFullYear();
  availableHolidays: HolidayRow[] = [];
  supervisors: Supervisor[] = [];
  selectedSupervisorId = '';
  isSubmitting = false;

  private gridApi: GridApi | null = null;

  // Columns: checkbox + Holiday Name + Date + Day
  colDefs: ColDef[] = [
    {
      headerName: '',
      field: 'select',
      width: 48,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    {
      headerName: 'Holiday Name',
      field: 'name',
      flex: 2,
      filter: 'agSetColumnFilter',
      cellStyle: CELL_STYLE,
    },
    {
      headerName: 'Date',
      field: 'date',
      flex: 1,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParamsLong,
      cellStyle: CELL_STYLE,
    },
    {
      headerName: 'Day',
      field: 'day',
      flex: 1,
      filter: 'agSetColumnFilter',
      cellStyle: CELL_STYLE,
    },
    // Hidden sort column — default descending sort by modifiedOn date (isoDate)
    { field: 'isoDate', hide: true, sort: 'desc', sortIndex: 0 },
  ];

  gridOptions = {
    suppressCellFocus: true,
    rowSelection: 'multiple' as const,
    suppressRowClickSelection: false,
    rowMultiSelectWithClick: true,
    pagination: false,
    domLayout: 'autoHeight' as const,
    headerHeight: 40,
    rowHeight: 40,
  };

  isUpdateMode = false;

  constructor(
    public dialogRef: MatDialogRef<ChooseOptionalHolidayDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChooseOptionalHolidayDialogData,
    private leaveService: LeaveService
  ) {
    this.availableHolidays = data?.availableHolidays ?? [];
    this.isUpdateMode = data?.isUpdateMode ?? false;
    this.selectedSupervisorId = data?.previousSupervisorId ?? '';
  }

  ngOnInit(): void {
    this.leaveService.getSupervisors().subscribe((sups) => {
      this.supervisors = sups;
    });
  }

  onGridReady(event: any): void {
    this.gridApi = event.api;
    // Pre-select rows when opening in update mode
    const preSelectedIds = this.data?.preSelectedIds ?? [];
    if (preSelectedIds.length > 0) {
      // Use setTimeout to ensure rows are rendered
      setTimeout(() => {
        this.gridApi?.forEachNode((node) => {
          if (preSelectedIds.includes(node.data?.id)) {
            node.setSelected(true);
          }
        });
      }, 0);
    }
  }

  get selectedSupervisorName(): string {
    return (
      this.supervisors.find((s) => s.id === this.selectedSupervisorId)
        ?.supervisorName ?? ''
    );
  }

  get selectedCount(): number {
    return this.gridApi?.getSelectedRows().length ?? 0;
  }

  removeSupervisor(): void {
    this.selectedSupervisorId = '';
  }

  get canSubmit(): boolean {
    return this.selectedCount > 0 && !!this.selectedSupervisorId;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (!this.canSubmit) return;
    const selected: HolidayRow[] = this.gridApi?.getSelectedRows() ?? [];
    const result: ChooseOptionalHolidayDialogResult = {
      selectedHolidayIds: selected.map((r) => r.id),
      supervisorId: this.selectedSupervisorId,
      supervisorName: this.selectedSupervisorName,
    };
    this.dialogRef.close(result);
  }
}
